import type { CSSProperties, ElementType, ReactNode } from 'react'

import { createElement, useEffect, useRef } from 'react'

import { gsap, isCompactViewport, prefersReducedMotion, registerGsap } from '@/lib/gsap'

interface SpinProps {
    as?: ElementType
    /** Seconds for a full 360° turn (hero gradient 30s, cone 10s). */
    duration?: number
    /** Spin direction. */
    reverse?: boolean
    className?: string
    style?: CSSProperties
    children?: ReactNode
    [key: string]: unknown
}

/**
 * Continuous linear rotation. Recreates Webflow's "Rotate Gradient Hero
 * Section" (a-68, 30s) and "Cone Image Rotate" (a-71, 10s) loops.
 */
export const Spin = ({
    as = 'div',
    duration = 30,
    reverse = false,
    className,
    style,
    children,
    ...rest
}: SpinProps) => {
    const ref = useRef<HTMLElement | null>(null)

    useEffect(() => {
        registerGsap()
        const el = ref.current
        if (!el) return
        if (prefersReducedMotion() || isCompactViewport()) return

        const ctx = gsap.context(() => {
            gsap.to(el, {
                rotation: reverse ? -360 : 360,
                duration,
                ease: 'none',
                repeat: -1
            })
        }, el)

        return () => ctx.revert()
    }, [duration, reverse])

    return createElement(as, { ref, className, style, ...rest }, children)
}
